import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
    ArrowLeft,
    Check,
    Clock3,
    Link2,
    MapPin,
    MessageSquare,
    Package,
    RefreshCw,
    RotateCcw,
    Truck,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ProductImage } from "@/components/catalog/ProductImage";
import { SubmitButton } from "@/components/form/SubmitButton";
import { orderApi, type OrderStatus } from "@/features/commerce/api";
import { trackingApi, type OrderTracking } from "@/features/delivery/api";
import { LiveMap } from "@/components/delivery/LiveMap";
import { ChatPanel } from "@/components/delivery/ChatPanel";
import { formatApiError } from "@/features/auth/formatError";
import { useAuth } from "@/features/auth/AuthContext";
import { useCart } from "@/features/cart/CartContext";
import { payWithRazorpay } from "@/features/payments/razorpay";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

const STAGES: Array<{ key: OrderStatus; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { key: "placed", label: "Order placed", icon: Check },
    { key: "confirmed", label: "Confirmed", icon: Check },
    { key: "packing", label: "Packing", icon: Package },
    { key: "out_for_delivery", label: "Out for delivery", icon: Truck },
    { key: "delivered", label: "Delivered", icon: Check },
];

function fINR(n: number) {
    return `₹${n.toLocaleString("en-IN")}`;
}

function fmtTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function OrderDetail() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { user } = useAuth();
    const { refresh: refreshCart } = useCart();

    const { data: order, isLoading, refetch } = useQuery({
        queryKey: ["order", id],
        queryFn: () => orderApi.get(id),
        refetchInterval: (query) => {
            const o = query.state.data;
            if (!o) return 5000;
            return o.status === "delivered" || o.status === "cancelled" ? false : 5000;
        },
    });

    const { data: tracking } = useQuery<OrderTracking>({
        queryKey: ["order", id, "tracking"],
        queryFn: () => trackingApi.order(id),
        enabled: Boolean(id),
        // Keep the previous tracking payload visible while re-fetching after
        // back-nav / focus change — otherwise the delivery OTP block blinks
        // out for a frame every time we return to the page.
        placeholderData: keepPreviousData,
        staleTime: 8000,
        refetchInterval: (q) => {
            const t = q.state.data;
            if (!t) return 8000;
            return t.status === "delivered" ? false : 8000;
        },
    });

    const [showChat, setShowChat] = useState(false);

    const cancelMutation = useMutation({
        mutationFn: () => orderApi.cancel(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["order", id] });
            qc.invalidateQueries({ queryKey: ["orders"] });
            toast.success("Order cancelled");
        },
        onError: (err) => toast.error(formatApiError(err)),
    });

    const retryMutation = useMutation({
        mutationFn: async () => {
            const updated = await orderApi.retryPayment(id);
            if (updated.payment?.method === "razorpay" && updated.payment?.gateway_order_id && updated.payment?.status !== "paid") {
                return payWithRazorpay(updated, {
                    name: user?.full_name,
                    email: user?.email,
                    contact: user?.phone || undefined,
                });
            }
            return updated;
        },
        onSuccess: async (updated) => {
            qc.invalidateQueries({ queryKey: ["order", id] });
            qc.invalidateQueries({ queryKey: ["orders"] });
            if (updated.payment?.status === "paid") {
                await refreshCart();
                toast.success("Payment successful");
            } else {
                toast(updated.payment?.error || "Retry initiated", {
                    description: "Please complete the payment.",
                });
            }
        },
        onError: (err) => {
            qc.invalidateQueries({ queryKey: ["order", id] });
            toast.error(formatApiError(err, "Retry failed"));
        },
    });

    const reorderMutation = useMutation({
        mutationFn: () => orderApi.reorder(id),
        onSuccess: (res) => {
            toast.success(res.message);
            navigate(ROUTES.cart);
        },
        onError: (err) => toast.error(formatApiError(err)),
    });

    if (isLoading || !order) {
        return (
            <div className="tsgo-container py-16 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                <p className="mt-3 text-sm text-muted-foreground">Loading order…</p>
            </div>
        );
    }

    const cancelable = !["out_for_delivery", "delivered", "cancelled"].includes(order.status);
    const paymentFailed =
        order.payment?.status === "failed" ||
        (order.payment?.status === "pending" && order.payment?.method !== "cod");
    const eta = fmtTime(order.estimated_delivery_at);

    const riderLoc = tracking?.rider?.current_location;
    const custGps = (order.address as unknown as { gps?: { lat: number; lng: number } }).gps;
    const showMap = Boolean(riderLoc || custGps);

    const onShare = async () => {
        try {
            const { token } = await trackingApi.createShare(order.id);
            const url = `${window.location.origin}/track/${token}`;
            if (navigator.share) {
                try {
                    await navigator.share({ title: `Track my TSGo order ${order.number}`, url });
                    return;
                } catch {
                    /* fall through to clipboard */
                }
            }
            await navigator.clipboard.writeText(url);
            toast.success("Tracking link copied", { description: url });
        } catch (err) {
            toast.error(formatApiError(err, "Couldn't create share link"));
        }
    };

    return (
        <div data-testid="order-detail-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-brand"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>

                <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="tsgo-h1" data-testid="order-detail-number">{order.number}</h1>
                        <p className="tsgo-lead mt-1">
                            Placed {new Date(order.placed_at).toLocaleString()} · {order.items.length} items · {fINR(order.breakdown.total)}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground/70 hover:border-brand/40 hover:text-brand"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                        </button>
                        <SubmitButton
                            type="button"
                            fullWidth={false}
                            variant="outline"
                            onClick={() => reorderMutation.mutate()}
                            loading={reorderMutation.isPending}
                            data-testid="order-reorder"
                        >
                            <RotateCcw className="mr-1 h-4 w-4" /> Reorder
                        </SubmitButton>
                        {paymentFailed && order.status !== "cancelled" ? (
                            <SubmitButton
                                type="button"
                                fullWidth={false}
                                onClick={() => retryMutation.mutate()}
                                loading={retryMutation.isPending}
                                className="bg-brand text-white hover:bg-brand-600"
                                data-testid="order-retry-payment"
                            >
                                Retry payment
                            </SubmitButton>
                        ) : null}
                        {cancelable ? (
                            <SubmitButton
                                type="button"
                                fullWidth={false}
                                onClick={() => cancelMutation.mutate()}
                                loading={cancelMutation.isPending}
                                className="bg-error text-white hover:bg-error/90"
                                data-testid="order-cancel"
                            >
                                <X className="mr-1 h-4 w-4" /> Cancel order
                            </SubmitButton>
                        ) : null}
                    </div>
                </div>
            </section>

            <section className="tsgo-container mt-8 grid gap-6 lg:grid-cols-[1fr_360px]">
                <div className="space-y-6">
                    {/* Rider card (once assigned) */}
                    {tracking?.rider ? (
                        <div
                            data-testid="order-rider-card"
                            className="rounded-2xl border border-brand/30 bg-gradient-to-br from-brand/5 to-white p-5 shadow-soft"
                        >
                            <div className="flex items-center gap-4">
                                {tracking.rider.photo_url ? (
                                    <img
                                        src={tracking.rider.photo_url}
                                        alt=""
                                        className="h-14 w-14 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="grid h-14 w-14 place-items-center rounded-full bg-brand text-white">
                                        <Truck className="h-6 w-6" />
                                    </span>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs uppercase tracking-widest text-brand">
                                        Your rider
                                    </p>
                                    <p className="text-lg font-bold" data-testid="order-rider-name">
                                        {tracking.rider.full_name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {tracking.rider.vehicle_type}
                                        {tracking.rider.vehicle_number ? ` · ${tracking.rider.vehicle_number}` : ""}
                                        {" · ★"} {tracking.rider.rating.toFixed(1)}
                                    </p>
                                </div>
                                <a
                                    href={`tel:${tracking.rider.phone}`}
                                    data-testid="order-rider-call"
                                    className="inline-flex h-10 items-center gap-2 rounded-full bg-brand px-4 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                                >
                                    Call
                                </a>
                            </div>
                            {tracking.delivery?.otp && tracking.status !== "delivered" ? (
                                <div
                                    data-testid="order-delivery-otp"
                                    className="mt-4 rounded-xl border border-brand/30 bg-white p-3 text-center"
                                >
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                        Share this OTP with your rider at handover
                                    </p>
                                    <p className="mt-1 text-3xl font-extrabold tracking-[0.6em] text-brand">
                                        {tracking.delivery.otp}
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Live map — visible once we have any coord to plot */}
                    {showMap ? (
                        <LiveMap
                            rider={riderLoc ? { lat: riderLoc.lat, lng: riderLoc.lng } : null}
                            customer={custGps ? { lat: custGps.lat, lng: custGps.lng } : null}
                            path={(tracking?.delivery as unknown as { path?: Array<{ lat: number; lng: number }> } | null)?.path?.filter(Boolean).map((p) => ({ lat: p.lat, lng: p.lng })) || []}
                            height={280}
                            testId="order-live-map"
                        />
                    ) : null}

                    {/* Share + chat actions */}
                    {tracking?.rider && order.status !== "delivered" && order.status !== "cancelled" ? (
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={onShare}
                                data-testid="order-share-link"
                                className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                            >
                                <Link2 className="h-3.5 w-3.5" /> Share live tracking
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowChat((v) => !v)}
                                data-testid="order-chat-toggle"
                                className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                            >
                                <MessageSquare className="h-3.5 w-3.5" />
                                {showChat ? "Hide chat" : "Chat with rider"}
                            </button>
                        </div>
                    ) : null}
                    {showChat ? <ChatPanel orderId={order.id} me="customer" /> : null}

                    {/* Timeline */}
                    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft sm:p-6" data-testid="order-timeline">
                        <div className="flex items-baseline justify-between gap-3">
                            <h3 className="tsgo-h3">Live tracking</h3>
                            {order.status === "cancelled" ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-error/10 px-2.5 py-1 text-xs font-bold text-error">
                                    Cancelled
                                </span>
                            ) : order.status === "pending_payment" ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-error/10 px-2.5 py-1 text-xs font-bold text-error">
                                    Payment pending
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-bold text-brand">
                                    <Clock3 className="h-3.5 w-3.5" /> ETA {eta}
                                </span>
                            )}
                        </div>

                        {order.status === "pending_payment" ? (
                            <div
                                className="mt-4 rounded-xl border border-error/30 bg-error/5 p-4"
                                data-testid="order-pending-payment-banner"
                            >
                                <p className="text-sm font-bold text-error">
                                    Payment was not completed. Your order is not confirmed.
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Retry the payment to confirm this order, or cancel it. Nothing
                                    has been sent to the store yet.
                                </p>
                            </div>
                        ) : order.status === "cancelled" ? (
                            <p className="mt-4 text-sm text-muted-foreground">
                                This order was cancelled on {order.cancelled_at ? new Date(order.cancelled_at).toLocaleString() : "—"}.
                            </p>
                        ) : (
                            <ol className="mt-6 space-y-4">
                                {STAGES.map((s, i) => {
                                    const ev = order.timeline.find((t) => t.status === s.key);
                                    const done = !!ev?.completed;
                                    const isCurrent = done && (i === STAGES.length - 1 || !order.timeline[i + 1]?.completed);
                                    const Icon = s.icon;
                                    return (
                                        <li key={s.key} className="flex items-start gap-3" data-testid={`stage-${s.key}`}>
                                            <span
                                                className={cn(
                                                    "grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 transition",
                                                    done
                                                        ? "border-brand bg-brand text-white"
                                                        : "border-border bg-background text-muted-foreground",
                                                    isCurrent && "animate-pulse ring-4 ring-brand/20",
                                                )}
                                            >
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <div className="flex-1">
                                                <p className={cn("text-sm font-semibold", done ? "text-foreground" : "text-muted-foreground")}>
                                                    {s.label}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {ev ? `${done ? "at" : "expected by"} ${fmtTime(ev.at)}` : ""}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </div>

                    {/* Items */}
                    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft sm:p-6" data-testid="order-items">
                        <h3 className="tsgo-h3">Items</h3>
                        <ul className="mt-4 divide-y divide-border/70">
                            {order.items.map((it) => (
                                <li key={it.product_id} className="flex items-center gap-3 py-3">
                                    <ProductImage product={it} size="thumb" className="h-12 w-12 rounded-lg border border-border/60" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{it.brand}</p>
                                        <p className="line-clamp-1 text-sm font-semibold">{it.name}</p>
                                        <p className="text-xs text-muted-foreground">{it.weight} · Qty {it.quantity}</p>
                                    </div>
                                    <p className="text-sm font-extrabold">{fINR(it.line_total)}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Right column */}
                <aside className="space-y-4">
                    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <h3 className="tsgo-h3">Deliver to</h3>
                        <div className="mt-3 flex items-start gap-3 text-sm">
                            <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand/10 text-brand">
                                <MapPin className="h-4 w-4" />
                            </span>
                            <div>
                                <p className="font-semibold capitalize">{String(order.address.label)}</p>
                                <p>{order.address.full_name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {order.address.line1}
                                    {order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city} — {order.address.pincode}
                                </p>
                                <p className="text-xs text-muted-foreground">{order.address.phone}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                        <h3 className="tsgo-h3">Bill details</h3>
                        <dl className="mt-3 space-y-1.5 text-sm">
                            <Row label="Item total" value={fINR(order.breakdown.subtotal)} />
                            <Row label="Delivery" value={order.breakdown.delivery_charge === 0 ? "FREE" : fINR(order.breakdown.delivery_charge)} />
                            <Row label="Platform fee" value={fINR(order.breakdown.platform_fee)} />
                            <Row label="Handling fee" value={fINR(order.breakdown.handling_fee)} />
                            <Row label="GST" value={fINR(order.breakdown.gst)} />
                            {order.breakdown.coupon_discount > 0 ? (
                                <Row label={`Coupon (${order.breakdown.coupon_code})`} value={`- ${fINR(order.breakdown.coupon_discount)}`} />
                            ) : null}
                            <div className="my-1.5 border-t border-border/70" />
                            <Row label="Total" value={fINR(order.breakdown.total)} bold />
                        </dl>
                        <p className="mt-3 text-xs text-muted-foreground" data-testid="order-payment-status">
                            {order.payment.status === "paid" ? "Paid via" : "Payment via"} <b>{(order.payment.method || "").toUpperCase()}</b> · {order.payment.status}
                        </p>
                        {paymentFailed && order.status !== "cancelled" ? (
                            <p className="mt-2 text-xs text-error" data-testid="order-payment-error">
                                {order.payment.error || "Payment is awaiting confirmation. Retry payment to check its status."}
                            </p>
                        ) : null}
                    </div>

                    <Button variant="outline" className="w-full" onClick={() => window.print()}>
                        Print invoice
                    </Button>
                </aside>
            </section>
        </div>
    );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
    return (
        <div className={cn("flex justify-between", bold && "text-base")}>
            <dt className={cn(bold ? "font-bold" : "text-muted-foreground")}>{label}</dt>
            <dd className={cn(bold ? "font-extrabold" : "font-semibold")}>{value}</dd>
        </div>
    );
}
